import React from "react";
import { SlidersHorizontal } from "lucide-react";
import { Button } from "../../atoms/Button/index.js";
import { Checkbox } from "../../atoms/Checkbox/index.js";
import { cn } from "../../lib/utils.js";
import type { TriggerSize } from "../../lib/trigger-size.js";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover/Popover.js";
import { Select } from "../Select/Select.js";
import { FilterOptionList } from "./FilterOptionList.js";
import type { FilterFieldDef } from "./filter-schema.js";
import type { FilterMenuController } from "./useFilterMenuController.js";

/* ---------------- FilterMenu (strategy §17.3, PB-2941) ----------------
   The complete filter affordance a module mounts as ONE component: trigger
   with active count, dialog popover, per-field controls rendered from the
   schema, and the Apply / Cancel / Clear-all footer. Draft edits never touch
   results — Apply commits, Cancel and outside-dismiss discard. The applied
   chip rail is the sibling FilterValueChips, bound to the same controller. */

export interface FilterMenuProps<Row = unknown> {
  readonly triggerRef?: React.Ref<HTMLButtonElement>;
  readonly controller: FilterMenuController<Row>;
  readonly triggerLabel?: string;
  /**
   * Size of the trigger button (`TriggerSize`, shared with ActionMenu).
   * `md` sits level with `md` Inputs and Selects — the named option for the
   * `[&>div>button]:h-9` wrapper module homes hand-roll today.
   */
  readonly triggerSize?: TriggerSize;
  /** Append the applied-filter count to the trigger ("Filter (2)"). Off when
   * the trigger label already names the selection, as an owners select does. */
  readonly showActiveCount?: boolean;
  readonly title?: string;
  readonly applyLabel?: string;
  readonly cancelLabel?: string;
  readonly clearLabel?: string;
  /** Display label for a value whose option may not be loaded (remote ids). */
  readonly valueLabel?: (
    fieldId: string,
    value: string,
  ) => React.ReactNode | undefined;
  readonly contentClassName?: string;
}

export function FilterMenu<Row = unknown>({
  triggerRef,
  controller,
  triggerLabel = "Filter",
  showActiveCount = true,
  title = "Filters",
  applyLabel = "Apply",
  cancelLabel = "Cancel",
  clearLabel = "Clear all",
  valueLabel,
  contentClassName,
  triggerSize = "sm",
}: FilterMenuProps<Row>): React.ReactElement {
  const { activeCount } = controller;
  return (
    <Popover
      contentRole="dialog"
      open={controller.open}
      onOpenChange={controller.setOpen}
    >
      <PopoverTrigger>
        <Button ref={triggerRef} variant="outline" size={triggerSize}>
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
          {triggerLabel}
          {showActiveCount && activeCount > 0 ? ` (${activeCount})` : ""}
        </Button>
      </PopoverTrigger>
      <PopoverContent padding="md" className={cn("w-80", contentClassName)}>
        <div className="flex flex-col gap-4 text-sm text-pb-text">
          <div className="text-heading-3 text-pb-text">{title}</div>
          <div className="flex flex-col gap-4">
            {controller.schema.map((field) => (
              <FilterMenuField
                key={field.id}
                field={field}
                values={controller.draft[field.id] ?? []}
                onChange={(values) =>
                  controller.setDraftValues(field.id, values)
                }
                valueLabel={valueLabel}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 border-t border-pb-border pt-3">
            <Button variant="ghost" size="sm" onClick={controller.clearDraft}>
              {clearLabel}
            </Button>
            <span className="flex-1" />
            <Button variant="ghost" size="sm" onClick={controller.cancel}>
              {cancelLabel}
            </Button>
            <Button variant="primary" size="sm" onClick={controller.apply}>
              {applyLabel}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FilterMenuField<Row>({
  field,
  values,
  onChange,
  valueLabel,
}: {
  readonly field: FilterFieldDef<Row>;
  readonly values: readonly string[];
  readonly onChange: (values: readonly string[]) => void;
  /** `| undefined` (not just optional): the parent forwards its own optional
   * prop verbatim, which exactOptionalPropertyTypes would otherwise reject. */
  readonly valueLabel?:
    | ((fieldId: string, value: string) => React.ReactNode | undefined)
    | undefined;
}): React.ReactElement {
  if (field.renderField) {
    return <>{field.renderField({ values, onChange })}</>;
  }

  if (field.options.kind === "remote") {
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-pb-text-secondary">
          {field.label}
        </span>
        <FilterOptionList
          source={field.options}
          label={field.label}
          multiple={field.type === "multi"}
          values={values}
          onChange={onChange}
          selectedLabel={(value) => valueLabel?.(field.id, value)}
        />
      </div>
    );
  }

  if (field.type === "single") {
    return (
      <Select
        label={field.label}
        options={[
          { value: "", label: field.anyLabel ?? "All" },
          ...field.options.options,
        ]}
        value={values[0] ?? ""}
        onChange={(value) => onChange(value ? [value] : [])}
      />
    );
  }

  return (
    <div
      role="group"
      aria-label={field.label}
      className="flex flex-col gap-1.5"
    >
      <span className="text-xs font-medium text-pb-text-secondary">
        {field.label}
      </span>
      {field.options.options.map((option) => (
        <Checkbox
          key={option.value}
          checked={values.includes(option.value)}
          onChange={(checked) =>
            onChange(
              checked
                ? [...values, option.value]
                : values.filter((value) => value !== option.value),
            )
          }
          label={option.label}
        />
      ))}
    </div>
  );
}
